import { ExternalLinkIcon, TagIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Banner, Button, ButtonLink } from 'slab'
import { getMinifiedLocaleCurrencyStringFromMoneyAmount } from 'utils'
import { usePostMarketplacePartnersHappiestBabyAddEmployee } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { events, MERCHANT_NAMES, PRICE_QUALIFIER_TEXT } from '../shared'
import CheckoutButton from './CheckoutButton'
import { DiscountedPrice } from './DiscountedPrice'
import { Formatted } from './Formatted'
import { PurchaseBanner } from './PurchaseBanner'
import { PurchaseConfirmationModal } from './PurchaseConfirmationModal'
import { VariantSelector } from './VariantSelector'
import { type MarketplaceProduct, type MarketplaceProductVariant, type SessionContextType } from '../shared/types'
import { getPriceCadenceSuffix } from '../shared/utils'

import { Title } from '.'

const MARKETPLACE_DISCLOSURE_ACK_KEY = 'hasAcknowledgedMarketplaceDisclosure'

type DetailsProps = {
  sessionContext: SessionContextType;
  product: MarketplaceProduct;
  selectedVariant: MarketplaceProductVariant;
  setSelectedVariant: (variant: MarketplaceProductVariant) => void;
  startTime: number;
  comingSoon: boolean;
  cardPaymentsEnabled: boolean;
}

export const Details = ({ sessionContext, product, selectedVariant, setSelectedVariant, startTime, comingSoon, cardPaymentsEnabled }: DetailsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [hasAcknowledgedMarketplaceDisclosure, setHasAcknowledgedMarketplaceDisclosure] = useState(false)
  const priceSuffix = getPriceCadenceSuffix(selectedVariant.price_cadence)
  const merchantName = product.marketplace_merchant_name
  const showLmnMessage = merchantName === MERCHANT_NAMES.TRUEMED
  const isHappiestBaby = merchantName === MERCHANT_NAMES.HAPPIEST_BABY
  const isPublicView = sessionContext.publicView

  // Happiest Baby API integration - clean mutation for API call only
  const happiestBabyMutation = usePostMarketplacePartnersHappiestBabyAddEmployee()

  const timeSinceView = () => Math.round((Date.now() - startTime) / 1000)

  useEffect(() => {
    if (isPublicView) return

    let acknowledged = false
    try {
      acknowledged = localStorage.getItem(MARKETPLACE_DISCLOSURE_ACK_KEY) === 'true'
    } catch (error) {
      console.error('Could not read from localStorage:', error)
    }
    setHasAcknowledgedMarketplaceDisclosure(acknowledged)
  }, [isPublicView])

  const handleSelectVariant = (variant: MarketplaceProductVariant) => {
    setSelectedVariant(variant)
    events.emitSelectVariantEvent(sessionContext, product, variant)
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
    events.emitPurchaseModalOpened(sessionContext, product, selectedVariant, timeSinceView())
  }

  const handleMarketplaceDisclosureAcknowledged = () => {
    if (hasAcknowledgedMarketplaceDisclosure) return
    try {
      localStorage.setItem(MARKETPLACE_DISCLOSURE_ACK_KEY, 'true')
      setHasAcknowledgedMarketplaceDisclosure(true)
    } catch (error) {
      console.error('Could not write to localStorage:', error)
    }
  }

  const handleCtaClick = () => {
    events.emitPurchaseClickedEvent(sessionContext, product, selectedVariant, timeSinceView())
  }

  const showDiscountedPrice = !isPublicView && selectedVariant.discounted_price !== null && selectedVariant.base_price !== null

  return (
    <div className='@container/details flex flex-col gap-6 items-stretch'>
      <div className='flex-col items-start'>
        {product.member_purchase && (
          <div className='mb-4'>
            <PurchaseBanner purchase={product.member_purchase} />
          </div>
        )}

        <Title product={product} classNameExtra='hidden @xl/product:flex' />

        {!isPublicView && (
          <div className='@xl/product:mt-1'>
            {selectedVariant.has_price_qualifier && (
              <span className='body-sm text-gray-800'>{PRICE_QUALIFIER_TEXT} </span>
            )}
            <div className='flex justify-between items-center self-stretch'>
              <div className='flex pr-4 gap-2 justify-start items-baseline'>
                <div className='flex gap-1 items-baseline'>
                  <h2 className='heading-sm'>
                    {getMinifiedLocaleCurrencyStringFromMoneyAmount(selectedVariant.discounted_price ?? selectedVariant.base_price)}
                  </h2>
                  {priceSuffix && <span className='body'>{priceSuffix}</span>}
                </div>
                {selectedVariant.discounted_price !== null && selectedVariant.base_price !== null && (
                  <p className='line-through'>
                    {getMinifiedLocaleCurrencyStringFromMoneyAmount(selectedVariant.base_price)}
                  </p>
                )}
                {showDiscountedPrice && selectedVariant.base_price !== null && selectedVariant.discounted_price !== null && (
                  <span className='hidden @sm/details:block'>
                    <DiscountedPrice
                      basePrice={selectedVariant.base_price}
                      discountedPrice={selectedVariant.discounted_price}
                      cadence={selectedVariant.price_cadence}
                      productSlug={product.slug}
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {showDiscountedPrice && selectedVariant.base_price !== null && selectedVariant.discounted_price !== null && (
          <span className='@sm/details:hidden'>
            <DiscountedPrice
              basePrice={selectedVariant.base_price}
              discountedPrice={selectedVariant.discounted_price}
              cadence={selectedVariant.price_cadence}
              productSlug={product.slug}
            />
          </span>
        )}

        <div className='mt-3'>
          <Formatted text={selectedVariant.description} />
        </div>
      </div>

      {product.marketplace_product_variants.length > 1 && (
        <VariantSelector
          variants={product.marketplace_product_variants}
          selectedVariant={selectedVariant}
          onSelectVariant={handleSelectVariant}
        />
      )}

      {selectedVariant.discount_instructions && (
        <Banner title={<Formatted text={selectedVariant.discount_instructions} />} icon={TagIcon} type='notice' />
      )}

      {!isPublicView && (
        <CtaButton
          hasAcknowledgedMarketplaceDisclosure={hasAcknowledgedMarketplaceDisclosure}
          selectedVariant={selectedVariant}
          isHappiestBaby={isHappiestBaby}
          merchantName={merchantName}
          productSlug={product.slug}
          handleCtaClick={handleCtaClick}
          handleOpenModal={handleOpenModal}
          comingSoon={comingSoon}
          cardPaymentsEnabled={cardPaymentsEnabled}
          balanceValue={sessionContext.balance?.value}
        />
      )}

      {/* TODO(yousef): use shared Modal component */}
      <PurchaseConfirmationModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); events.emitPurchaseModalClosed(sessionContext, product, selectedVariant) }}
        merchantName={merchantName}
        redirectUri={selectedVariant.redirect_uri}
        discountText={selectedVariant.discount_instructions}
        showLmnMessage={showLmnMessage}
        emitPurchaseClickedEvent={handleCtaClick}
        emitMarketplaceDisclosureViewed={() => events.emitMarketplaceDisclosureViewed(sessionContext, product, selectedVariant)}
        handleMarketplaceDisclosureAcknowledged={handleMarketplaceDisclosureAcknowledged}
        isHappiestBaby={isHappiestBaby}
        happiestBabyMutation={happiestBabyMutation}
      />

      {/* TODO(yousef) look into a better way to flag Truemed */}
      {product.marketplace_merchant_name === MERCHANT_NAMES.TRUEMED && (
        <>
          <div className='flex flex-col gap-2 items-start'>
            <p className='font-semibold'>How Truemed works</p>
            <p className='text-gray-500'>Thatch partners with Truemed so you can use your Thatch balance on products that support your health and help prevent or manage chronic conditions.</p>
            <p className='text-gray-500'>When you shop with Truemed brands, you’ll go through a quick, secure process to see if you qualify for a Letter of Medical Necessity (LMN). If approved, a licensed provider issues an LMN, so you can easily use your Thatch balance for qualifying purchases. Just remember to upload your LMN to Thatch so we can review and approve it on our end, too!</p>
          </div>
          <div className='h-px bg-gray-200' />
        </>
      )}
    </div>
  )
}

const CtaButton = ({
  hasAcknowledgedMarketplaceDisclosure,
  selectedVariant,
  isHappiestBaby,
  merchantName,
  productSlug,
  handleCtaClick,
  handleOpenModal,
  comingSoon,
  cardPaymentsEnabled,
  balanceValue,
}: {
  hasAcknowledgedMarketplaceDisclosure: boolean;
  selectedVariant: MarketplaceProductVariant;
  isHappiestBaby: boolean;
  merchantName: string;
  productSlug: string;
  handleCtaClick: () => void;
  handleOpenModal: () => void;
  comingSoon: boolean;
  cardPaymentsEnabled: boolean;
  balanceValue?: string;
}) => {
  const happiestBabyMutation = usePostMarketplacePartnersHappiestBabyAddEmployee()
  const { assign } = useWindowLocation()

  // Show disabled button for coming soon products
  if (comingSoon) {
    return (
      <Button
        size='large'
        variant='primary'
        width='full'
        disabled
      >
        Coming soon
      </Button>
    )
  }

  // For hosted checkout, navigate to checkout page
  if (selectedVariant.hosted_checkout) {
    const handleHostedCheckoutClick = () => {
      handleCtaClick()
      assign(`/members/marketplace/${productSlug}/checkout?variant_id=${selectedVariant.id}`)
    }

    return (
      <CheckoutButton
        cardPaymentsEnabled={cardPaymentsEnabled}
        balanceValue={balanceValue}
        variantPrice={selectedVariant.discounted_price ?? selectedVariant.base_price}
        onClick={handleHostedCheckoutClick}
      />
    )
  }

  const getStartedText = `Get started with ${merchantName}`

  if (hasAcknowledgedMarketplaceDisclosure && isHappiestBaby) {
    const handleHappiestBabyRegistration = () => {
      happiestBabyMutation.mutate(undefined, {
        onSuccess: () => {
          handleCtaClick()
          window.open(selectedVariant.redirect_uri, '_blank', 'noopener')
        },
        onError: () => {
          toast.error('Failed to register with Happiest Baby')
        }
      })
    }

    return (
      <Button
        onClick={handleHappiestBabyRegistration}
        disabled={happiestBabyMutation.isPending}
        rightIcon={ExternalLinkIcon}
        size='large'
        variant='primary'
        width='full'
      >
        {happiestBabyMutation.isPending ? 'Registering...' : getStartedText}
      </Button>
    )
  }

  if (hasAcknowledgedMarketplaceDisclosure) {
    return (
      <ButtonLink
        href={selectedVariant.redirect_uri}
        target='_blank'
        rel='noopener'
        rightIcon={ExternalLinkIcon}
        size='large'
        variant='primary'
        width='full'
        onClick={handleCtaClick}
      >
        {getStartedText}
      </ButtonLink>
    )
  }

  return (
    <Button
      onClick={handleOpenModal}
      size='large'
      variant='primary'
      width='full'
    >
      {getStartedText}
    </Button>
  )
}
